/**
 * DateTimePicker Component
 *
 * A combined date and time picker using Calendar popover and time dropdown.
 * Time options are in 15-minute increments from 00:00 to 23:45.
 */

import * as React from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { CalendarIcon, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DateTimePickerProps {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  showTime?: boolean;
}

// Generate time options in 15-minute increments
const TIME_OPTIONS: { value: string; label: string }[] = [];
for (let hour = 0; hour < 24; hour++) {
  for (let minute = 0; minute < 60; minute += 15) {
    const h = hour.toString().padStart(2, '0');
    const m = minute.toString().padStart(2, '0');
    TIME_OPTIONS.push({
      value: `${h}:${m}`,
      label: `${h}:${m}`,
    });
  }
}

export function DateTimePicker({
  date,
  onDateChange,
  placeholder = 'Kies datum',
  disabled = false,
  className,
  showTime = true,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  // Extract time from date
  const timeValue = date
    ? `${date.getHours().toString().padStart(2, '0')}:${Math.floor(date.getMinutes() / 15) * 15 === 0 ? '00' : (Math.floor(date.getMinutes() / 15) * 15).toString().padStart(2, '0')}`
    : '';

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      onDateChange(undefined);
      return;
    }

    // Preserve existing time if date already has one
    if (date) {
      selectedDate.setHours(date.getHours(), date.getMinutes(), 0, 0);
    } else {
      // Default to 09:00 for new dates
      selectedDate.setHours(9, 0, 0, 0);
    }

    onDateChange(selectedDate);
  };

  const handleTimeChange = (time: string) => {
    if (!date) return;

    const [hours, minutes] = time.split(':').map(Number);
    const newDate = new Date(date);
    newDate.setHours(hours, minutes, 0, 0);
    onDateChange(newDate);
  };

  const handleClear = () => {
    onDateChange(undefined);
    setIsOpen(false);
  };

  return (
    <div className={cn('flex gap-2', className)}>
      {/* Date Picker */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'flex-1 justify-start text-left font-normal',
              !date && 'text-muted-foreground'
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, 'PPP', { locale: nl }) : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            initialFocus
            locale={nl}
          />
          <div className="border-t p-2 flex justify-between">
            <Button variant="ghost" size="sm" onClick={handleClear}>
              Wissen
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
              Sluiten
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Time Picker */}
      {showTime && date && (
        <Select value={timeValue} onValueChange={handleTimeChange} disabled={disabled}>
          <SelectTrigger className="w-24">
            <Clock className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Tijd" />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {TIME_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

/**
 * Simple Date Picker (without time)
 */
export function DatePicker({
  date,
  onDateChange,
  placeholder = 'Kies datum',
  disabled = false,
  className,
}: Omit<DateTimePickerProps, 'showTime'>) {
  return (
    <DateTimePicker
      date={date}
      onDateChange={onDateChange}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      showTime={false}
    />
  );
}
